import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useBranch } from '@/features/branch/branch-context';
import { api } from '@/lib/api';

export type ChronicCondition = 'diabetes' | 'hypertension' | 'asthma';
export type CheckType = 'eye' | 'foot' | 'uacr' | 'act' | 'asthma_review';

export interface ChronicConfig {
  approved: boolean;
  approvedAt: string | null;
  approvedBy: string | null;
  source: string;
  diabetes: {
    codes: string[];
    hba1cTargetPct: number;
    bpTarget: { systolic: number; diastolic: number };
    hba1cEveryMonths: number;
    eyeEveryMonths: number;
    footEveryMonths: number;
    uacrEveryMonths: number;
  };
  hypertension: {
    codes: string[];
    bpTarget: { systolic: number; diastolic: number };
    reviewEveryMonths: number;
  };
  asthma: {
    codes: string[];
    actWellControlledMin: number;
    reviewEveryMonths: number;
  };
  heartRisk: { moderatePct: number; highPct: number };
}

export interface Due {
  lastAt: string | null;
  dueAt: string | null;
  overdue: boolean;
}
export interface PatientRef {
  id: string;
  fullName: string;
}
export interface Bp {
  systolic: number | null;
  diastolic: number | null;
  at: string;
  onTarget: boolean;
}
interface RowBase {
  enrolmentId: string;
  patient: PatientRef;
  condition: ChronicCondition;
  enrolledAt: string;
  hasCarePlan: boolean;
}
export interface DiabetesRow extends RowBase {
  hba1c: Due & {
    value: string | null;
    unit?: string | null;
    at?: string;
    onTarget?: boolean | null;
  };
  bp: Bp | null;
  eye: Due & { value: string | null };
  foot: Due & { value: string | null };
  uacr: Due & { value: string | null };
}
export interface HypertensionRow extends RowBase {
  bp: Bp | null;
  review: Due;
}
export interface AsthmaRow extends RowBase {
  act: {
    score: number | null;
    at: string;
    wellControlled: boolean | null;
  } | null;
  inhalers: string | null;
  actionPlanAt: string | null;
  review: Due;
}
export type RegisterRow = DiabetesRow | HypertensionRow | AsthmaRow;
export interface Register<R> {
  config: ChronicConfig;
  rows: R[];
}
export interface Suggestion {
  patient: PatientRef;
  condition: ChronicCondition;
  diagnosis: { code: string; description: string; at: string };
}
export interface Overdue {
  enrolmentId: string;
  patient: PatientRef;
  what: string;
  dueAt: string | null;
}
export interface HeartRiskRow {
  patient: PatientRef;
  assessment: {
    inputs: HeartRiskInput;
    riskPct: number | null;
    category: 'low' | 'moderate' | 'high' | null;
    configApproved: boolean;
    recordedAt: string;
  } | null;
}
export interface HeartRiskInput {
  sex: 'F' | 'M';
  age: number;
  totalCholMmol: number;
  hdlMmol: number;
  systolic: number;
  treated: boolean;
  smoker: boolean;
  diabetes: boolean;
}
export interface RefillRow {
  patient: PatientRef;
  rows: {
    medicine: string;
    frequency: string;
    lastDispensedAt: string;
    quantity: number;
    nextRefill: string | null;
    missed: boolean;
  }[];
  inSync: boolean;
  refillDay: string | null;
}
export interface RamadanRow {
  patient: PatientRef;
  assessment: {
    category: 'low' | 'moderate' | 'high';
    factors: string;
    recordedAt: string;
  } | null;
}
export interface CarePlan {
  patient: PatientRef;
  plan: {
    goals: string;
    nextSteps: string;
    showInApp: boolean;
    /** RL-996: name the register conditions in the patient app (default off). */
    showConditions: boolean;
    /** RL-996: the doctor the app shows as "Agreed with"; alerts route here. */
    ownerDoctor: { id: string; fullName: string } | null;
    updatedAt: string;
    updatedBy: string | null;
  } | null;
}

export interface CarePlanInput {
  patientId: string;
  goals: string;
  nextSteps: string;
  showInApp: boolean;
  showConditions: boolean;
  /** null clears the owner (the app then shows the last doctor who saved). */
  ownerDoctorId: string | null;
}

/** Permissions that may read GET /doctors (the API's RequireAnyPermission). */
export const DOCTOR_LIST_PERMISSIONS = [
  'appointments:manage',
  'calendar:read',
  'schedules:manage',
  // RL-1856: the care team (doctors included) picks any branch doctor as owner.
  'chronic_care:manage',
] as const;

function useChronicQuery<T>(key: string, path: string, enabled = true) {
  const { selected } = useBranch();
  return useQuery({
    queryKey: ['chronic', key, selected?.id ?? 'all'],
    enabled,
    queryFn: ({ signal }) => api.get<T>(path, { signal }).then((r) => r.data),
  });
}

export const useChronicConfig = () =>
  useChronicQuery<ChronicConfig>('config', '/chronic/config');
export const useRegister = <R extends RegisterRow>(c: ChronicCondition) =>
  useChronicQuery<Register<R>>(`register-${c}`, `/chronic/registers/${c}`);
export const useSuggestions = () =>
  useChronicQuery<Suggestion[]>('suggestions', '/chronic/suggestions');
export const useOverdue = () =>
  useChronicQuery<Overdue[]>('overdue', '/chronic/overdue');
export const useHeartRisk = () =>
  useChronicQuery<Register<HeartRiskRow>>('heart-risk', '/chronic/heart-risk');
export const useRefills = () =>
  useChronicQuery<RefillRow[]>('refills', '/chronic/refills');
export const useRamadan = () =>
  useChronicQuery<RamadanRow[]>('ramadan', '/chronic/ramadan');
/** Doctors working at the selected branch (GET /doctors) for the plan owner. */
export function useBranchDoctorList(enabled: boolean) {
  const { selected } = useBranch();
  return useQuery({
    queryKey: ['doctors', selected?.id ?? 'all'],
    enabled,
    staleTime: 5 * 60_000,
    queryFn: ({ signal }) =>
      api
        .get<{ id: string; fullName: string }[]>('/doctors', { signal })
        .then((r) => r.data),
  });
}

export const useCarePlan = (patientId: string | null) =>
  useChronicQuery<CarePlan>(
    `care-plan-${patientId}`,
    `/chronic/care-plans/${patientId}`,
    !!patientId,
  );

/** Any chronic write; refreshes every chronic view on success. */
export function useChronicAction<V, R = unknown>(run: (v: V) => Promise<R>) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: run,
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['chronic'] }),
  });
}

export const chronicApi = {
  enrol: (v: { patientId: string; condition: ChronicCondition }) =>
    api.post('/chronic/enrolments', v),
  update: (v: { id: string; inhalers?: string; actionPlanAt?: string }) =>
    api.patch(`/chronic/enrolments/${v.id}`, {
      inhalers: v.inhalers,
      actionPlanAt: v.actionPlanAt,
    }),
  end: (v: { id: string; reason: string }) =>
    api.post(`/chronic/enrolments/${v.id}/end`, { reason: v.reason }),
  check: (v: {
    id: string;
    checkType: CheckType;
    value?: string;
    doneAt: string;
  }) =>
    api.post(`/chronic/enrolments/${v.id}/checks`, {
      checkType: v.checkType,
      value: v.value || undefined,
      doneAt: v.doneAt,
    }),
  remind: (v: { enrolmentId: string; what: string }) =>
    api
      .post<{
        status: 'simulated' | 'suppressed';
        suppressedReason: string | null;
      }>('/chronic/overdue/remind', v)
      .then((r) => r.data),
  carePlan: (v: CarePlanInput) =>
    api
      .put<CarePlan>(`/chronic/care-plans/${v.patientId}`, {
        goals: v.goals,
        nextSteps: v.nextSteps,
        showInApp: v.showInApp,
        showConditions: v.showConditions,
        ownerDoctorId: v.ownerDoctorId,
      })
      .then((r) => r.data),
  heartRisk: (v: HeartRiskInput & { patientId: string }) =>
    api.post('/chronic/heart-risk', v),
  ramadan: (v: { patientId: string; category: string; factors: string }) =>
    api.post('/chronic/ramadan', v),
  sync: (v: { patientId: string; refillDate: string }) =>
    api.post(`/chronic/refills/${v.patientId}/sync`, {
      refillDate: v.refillDate,
    }),
  approve: (v: { note: string }) => api.post('/chronic/config/approve', v),
};
